import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';

import { useLandingPage } from '../../contexts/LandingPageContext';
import { useAuth } from '../../contexts/AuthContext';
import { ADMIN_SECTIONS, getAdminSection } from './sectionsRegistry';
import LivePreview from '../LivePreview';
import { notifyPreviewRefresh } from '../../lib/previewBus';
import * as defaultSections from '../../data/defaultSections';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { editLanguage, setEditLanguage, updateSection, saveSection } = useLandingPage();
  const { logout } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [previewVisible, setPreviewVisible] = useState(true);

  const pathMatch = location.pathname.match(/\/dashboard\/([^/]+)/);
  const activeId = pathMatch ? pathMatch[1] : null;
  const activeSection = getAdminSection(activeId);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  /** Restore one section to its defaults and persist immediately. */
  const resetSection = async (section) => {
    const data = defaultSections[section.defaultKey];
    if (!data) return;
    updateSection(section.stateKey, data);
    await saveSection(section.stateKey, data);
  };

  const handleResetAll = async () => {
    if (!window.confirm(t('dashboard.confirmReset'))) return;
    for (const section of ADMIN_SECTIONS) {
      await resetSection(section);
    }
    notifyPreviewRefresh();
    alert(t('dashboard.successMessage'));
  };

  return (
    <div className={`db-shell${sidebarOpen ? ' db-sidebar-open' : ' db-sidebar-collapsed'}`}>
      <header className="db-topbar">
        <div className="db-topbar-left">
          <button
            type="button"
            className="db-hamburger"
            onClick={() => setSidebarOpen(open => !open)}
            aria-label="Toggle sidebar"
            aria-expanded={sidebarOpen}
          >
            <Menu size={20} strokeWidth={2} />
          </button>
          <Link to="/dashboard" className="db-brand">
            <span className="db-brand-icon">
              <svg viewBox="0 0 262.7 262.7" preserveAspectRatio="xMidYMid meet">
                <g><path stroke="#222" strokeWidth="10" strokeMiterlimit="10" fill="#222" d="m77.9 50.9-57.3 57.3 5 4.9 52.3-52.3 50.9 51v24.6H81.2v7.1h47.6v74.1L79.3 168H65.7v7h10.7l59.4 59.5v-91h38.1l-17.8 17.8-5 4.9 23.4 23.4 4.9-5-18.4-18.4 29.8-29.8h-55V112L187 60.9l52.2 52.2 4.9-4.9L187 51l-51.2 51.1V27.6h-7v74.2L77.9 50.9z"/></g>
              </svg>
            </span>
            <span className="db-brand-text">{t('dashboard.brand')}</span>
          </Link>
        </div>

        <div className="db-topbar-center">
          <span className="db-current-lang">{t('dashboard.currentLang')}</span>
          <div className="db-lang-toggle">
            <button
              type="button"
              className={`db-lang-pill${editLanguage === 'de' ? ' db-lang-pill-active' : ''}`}
              onClick={() => setEditLanguage('de')}
            >
              {t('dashboard.de')}
            </button>
            <button
              type="button"
              className={`db-lang-pill${editLanguage === 'en' ? ' db-lang-pill-active' : ''}`}
              onClick={() => setEditLanguage('en')}
            >
              {t('dashboard.en')}
            </button>
          </div>
        </div>

        <div className="db-topbar-right">
          <button
            type="button"
            className={`db-toolbar-btn${previewVisible ? ' db-toolbar-btn-active' : ''}`}
            onClick={() => setPreviewVisible(visible => !visible)}
          >
            {t('dashboard.preview')}
          </button>
          <button type="button" className="db-toolbar-btn db-logout-btn" onClick={handleLogout}>
            {t('dashboard.logout')}
          </button>
        </div>
      </header>

      <div className="db-body">
        <aside className={`db-sidebar${sidebarOpen ? '' : ' db-sidebar-hidden'}`}>
          <div className="db-sidebar-header">
            <h4>{t('dashboard.sections')}</h4>
          </div>
          <nav className="db-section-list">
            {ADMIN_SECTIONS.map(section => (
              <Link
                key={section.id}
                to={`/dashboard/${section.id}`}
                title={t('dashboard.sectionLabels.' + section.id)}
                className={`db-section-item${section.id === activeId ? ' db-section-item-active' : ''}`}
              >
                <span className="db-section-icon">
                  <section.icon size={18} strokeWidth={1.8} />
                </span>
                <span className="db-section-text">
                  <span className="db-section-label">{t('dashboard.sectionLabels.' + section.id)}</span>
                  <span className="db-section-description">{t('dashboard.sectionDescriptions.' + section.id)}</span>
                </span>
              </Link>
            ))}
          </nav>
          <div className="db-sidebar-footer">
            <button type="button" className="db-reset-all" onClick={handleResetAll}>
              {t('dashboard.loadDefaults')}
            </button>
          </div>
        </aside>

        <main className="db-main">
          <section className="db-editor">
            <div className="db-editor-header">
              <div className="db-editor-title">
                <h3>
                  {activeSection
                    ? `${t('dashboard.sectionLabels.' + activeSection.id)} · ${t('dashboard.edit')}`
                    : t('dashboard.edit')}
                </h3>
              </div>
            </div>
            <div className="db-editor-content">
              <Routes>
                <Route
                  index
                  element={
                    <div className="db-empty-state">
                      <p>{t('dashboard.noSectionSelected')}</p>
                    </div>
                  }
                />
                {ADMIN_SECTIONS.map(section => (
                  <Route key={section.id} path={section.id} element={<section.Component />} />
                ))}
              </Routes>
            </div>
          </section>

          {previewVisible && (
            <section className="db-preview">
              <LivePreview />
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
